/**
 * Module that provides campaign definitions,
 * governance rules & message metadata from
 * targeted messaging service.
 */

import { Api, ICampaignDefinitions } from "@ms-ofb/floodgate-shared-typings";
import { MessageSpec } from "@ms-ofb/officefloodgatecore/dist/src/Governance/MessageMetadata";
import { UserGovernanceRules } from "@ms-ofb/officefloodgatecore/dist/src/Governance/UserGovernanceRules";

import { ContentResponse } from "@ms-ofb/officepersonalization/dist/lib/Common/ContentResponse";
import { ContentType } from "@ms-ofb/officepersonalization/dist/lib/Common/ContentType";
import { IUserFact } from "@ms-ofb/officepersonalization/dist/lib/Common/Models";
import { getDynamicSetting, SettingKey } from "@ms-ofb/officepersonalization/dist/lib/Configuration/DynamicConfiguration";
import { setLogger } from "@ms-ofb/officepersonalization/dist/lib/Logging/Logger";
import { 
	getDebugCampaignDefinitions, 
	getDebugUserFacts, 
	TargetedMessagingClient 
} from "@ms-ofb/officepersonalization/dist/lib/TargetedMessaging/TargetedMessagingClient";
import { TULIPS_LOAD_TIMEOUT } from "@ms-ofb/officepersonalization/dist/lib/TargetedMessaging/TargetedMessagingConstants";
import { getCachedContent } from "@ms-ofb/officepersonalization/dist/lib/Utils/Caching";
import * as Configuration from "../Configuration/Configuration";
import { IBeaconParameters } from "../FloodgateCore/GenericMessagingLauncherFactory";
import { createTimeoutPromise } from "../Utils";
import { PersonalizationLogger } from "./PersonalizationLogger";

export class AssetsProvider implements Api.IAssetsProvider {
	private configuration: Configuration.Configuration;
	private tmsgClient: TargetedMessagingClient;

	public constructor() {
		this.configuration = Configuration.get();
		setLogger(new PersonalizationLogger());
		// Enable TargetedMessaging client only if authCallback is provided
		// This is for cases where existing Floodgate hosts are not yet on-boarded into web surfaces flow
		const floodgateInitOptions = this.configuration && this.configuration.getFloodgateInitOptions();
		const commonInitOptions = this.configuration && this.configuration.getCommonInitOptions();
		const authTokenCallback = floodgateInitOptions && floodgateInitOptions.authTokenCallback;
		if (authTokenCallback && (authTokenCallback.getAuthToken || authTokenCallback.getUserPuid)) {
			const initOptions = this.configuration.getCommonInitOptions();
			this.tmsgClient = new TargetedMessagingClient(
				initOptions.appId.toString(),
				"Web" /*platform*/,
				initOptions.build,
				initOptions.originalLocale,
				this.extractCountryFromLocale(initOptions.originalLocale),
				this.configuration.getSdkSessionId(),
				floodgateInitOptions.authTokenCallback,
				floodgateInitOptions.campaignFlights,
				floodgateInitOptions.campaignQueryParameters,
				commonInitOptions.ageGroup,
				commonInitOptions.authenticationType,
				commonInitOptions.telemetryGroup?.audienceGroup || commonInitOptions.telemetryGroup?.audience,
				floodgateInitOptions.messagingServiceSettings
			);
		}
	}

	/**
	 * Get campaign definitions from TMS
	 */
	public getCampaignDefinitions(): Promise<ICampaignDefinitions> {
		const debugResponse = getDebugCampaignDefinitions();
		if (debugResponse && debugResponse.content) {
			return Promise.resolve(debugResponse.content);
		}

		if (!this.tmsgClient) {
			return Promise.resolve(null);
		}

		return this.tmsgClient.getContent(ContentType.campaignContent)
			.then(({ content }: ContentResponse) => content);
	}

	public getCachedCampaignDefinitions(): ICampaignDefinitions {
		return getCachedContent(ContentType.campaignContent);
	}

	public getUserGovernanceRules(): Promise<UserGovernanceRules> {
		if (!this.tmsgClient) {
			return Promise.resolve(null);
		}

		return this.tmsgClient.getContent(ContentType.userGovernanceRules)
			.then(({ content }: ContentResponse) => content);
	}

	public getMessageMetadata(): Promise<MessageSpec> {
		if (!this.tmsgClient) {
			return Promise.resolve(null);
		}

		return this.tmsgClient.getContent(ContentType.messageMetadata)
			.then(({ content }: ContentResponse) => content);
	}

	/**
	 * Get TULIPS user facts from GWS endpoint
	 */
	public getUserFacts(): Promise<IUserFact[]> {
		const debugResponse = getDebugUserFacts();
		if (debugResponse && debugResponse.content) {
			return Promise.resolve(debugResponse.content);
		}

		if (!this.tmsgClient) {
			return Promise.resolve(null);
		}

		// Try to get facts from TMS
		if (this.isTulipsServiceEnabled()) {
			const timeoutMilliseconds = getDynamicSetting(SettingKey.tulipsLoadTimeout, TULIPS_LOAD_TIMEOUT);
			const timeoutPromise = createTimeoutPromise(timeoutMilliseconds, this.getTulipsServiceUserFacts());
			return timeoutPromise
				.then(userFacts => userFacts)
				.catch(() => this.getSubstrateUserFacts());
		}

		return this.getSubstrateUserFacts();
	}

	public setCampaignAction(beaconParameters: IBeaconParameters): Promise<boolean> {
		if (!this.tmsgClient) {
			return;
		}

		const initOptions = this.configuration.getCommonInitOptions();
		const floodgateInitOptions = this.configuration.getFloodgateInitOptions();
		return this.tmsgClient.sendBeaconRequestToService(
			initOptions.appId.toString(),
			beaconParameters,
			"Web" /*platform*/,
			initOptions.build,
			initOptions.originalLocale,
			this.extractCountryFromLocale(initOptions.originalLocale),
			this.configuration.getSdkSessionId(),
			floodgateInitOptions.authTokenCallback);
	}

	// Floodgate doesn't have country, so we have to use locale as a proxy and extract the country code
	private extractCountryFromLocale(locale?: string): string {
		if (!locale) {
			return undefined;
		}

		const splitLocale = locale.split("-");
		if (splitLocale.length === 2) {
			return splitLocale[1];
		} else {
			return undefined;
		}
	}

	private getSubstrateUserFacts(): Promise<IUserFact[]> {
		if (!this.tmsgClient) {
			return Promise.resolve(null);
		}

		return this.tmsgClient.getUserFactsContent(ContentType.userFacts)
			.then(({ content }: ContentResponse) => content);
	}

	private getTulipsServiceUserFacts(): Promise<IUserFact[]> {
		if (!this.tmsgClient) {
			return Promise.resolve(null);
		}

		return this.tmsgClient.getTulipsServiceUserFactsContent();
	}

	private isTulipsServiceEnabled(): boolean {
		return this.configuration?.getFloodgateInitOptions()?.userFactsReadConfig?.enabled;
	}
}

let assetsProvider: AssetsProvider;

export function get() {
	if (!assetsProvider) {
		assetsProvider = new AssetsProvider();
	}

	return assetsProvider;
}
